import data from './defaultOverlayZones.json';

const { width: FIELD_W, height: FIELD_H } = data.field_size;
const HAND_MIN_H = data.hand_min_height;
const LAYER_ZONES = data.layer_zones;

/** 同梱マットのフィールドの大きさと、フィールドのレイヤーの中のゾーン（鍵 `種類:owner:番号`。owner 1 は 180° 回したレイヤーの座標） */
export { FIELD_W, FIELD_H, LAYER_ZONES };

/**
 * 動画の縦のうち盤（2 人ぶんのフィールド）の範囲。上下の手札は同じ高さと仮定する。{ y0, y1, hand }
 * 積み順は hand2 / field2 / field1 / hand1 で field レイヤーの高さは同梱マットの FIELD_H（`defaultOverlayZones` と同じ前提）。
 * 再現エンジンの入力画面（`ocgInput/stage.js`）が使う（Issue #496 で ocgInput/common.js から移した）
 */
export function fieldCrop(videoSize, fieldH = FIELD_H) {
  const { w, h } = videoSize ?? {};
  if (!(w > 0) || !(h > 0)) return null;
  const rest = h - fieldH * 2;
  const hand = rest >= 0 ? rest / 2 : 0;
  return { y0: hand, y1: hand + Math.min(h, fieldH * 2), hand };
}

/**
 * その config で「動画の寸法だけからの推定」が成り立つか。
 * 成り立たない理由があれば文字列で返す（そのままUIに出す）。
 *
 * @param {object|null} config GetFieldGenConfig の中身
 * @returns {string|null} 問題が無ければ null
 */
export function configGuessIssue(config) {
  const configs = config?.configs ?? [];
  if (configs.length === 0) return null;   // 判断材料が無い。寸法側の検査に任せる

  // field レイヤーのサイズが違うと枠の位置がそのまま変わるが、合成の寸法からは
  // 判別できないことがある（_resolve_layer_sizes はキャンバスを
  // max(基準, playmat_*) で決めるので、基準より小さいマットでも合成サイズは
  // 変わらない）。config が手元にあるなら見て弾く。
  const fields = configs.filter((c) => c?.field_type === 'field');
  const bundled = fields.every((c) => (
    (c.playmat_width ?? FIELD_W) === FIELD_W
    && (c.playmat_height ?? FIELD_H) === FIELD_H
  ));
  if (fields.length > 0 && !bundled) {
    return '同梱以外のプレイマットです。config-pointsで調整してください';
  }

  // 手札レイヤーの**幅**は見なくてよい。合成の幅が広がるぶんは
  // defaultOverlayZones() がパディングとして正しく扱う。
  //
  // **高さは上下で揃っている必要がある。** 積み順は hand2 / field2 / field1 /
  // hand1 なので field2 の上端は hand2 の高さそのもので、動画の高さを2等分した
  // 値ではない。片側だけ設定されている場合も _build_layer_plan が反対側を
  // 既定の 270 で埋めるので同じズレになる（1枠の半分近くズレうる）。
  const hands = configs.filter((c) => c?.field_type === 'hand');
  if (hands.length > 0) {
    const heightOf = (player) => {
      const e = hands.find((c) => c.player_number === player);
      return Math.max(HAND_MIN_H, e?.playmat_height ?? 0);
    };
    if (heightOf(1) !== heightOf(2)) {
      return '上下の手札レイヤーの高さが違う構成です。config-pointsで調整してください';
    }
  }
  return null;
}

/**
 * config に overlay_zones が無いときの既定のゾーン位置を、動画の寸法から求める。
 *
 * overlay_zones は config-points で4点を合わせ直したときに焼き込まれるので、
 * それ以前に作られた試合には入っていない。同梱プレイマットを使っている限り
 * 枠の位置は決まっているので、合成の構成を寸法から復元して当てる。
 *
 * 合成は field_image.py の _build_layer_plan により
 *   手札あり: hand2 / field2 / field1 / hand1 を縦積み
 *   手札なし: field2 / field1
 * で、field レイヤーの高さは常に 630（同梱マット使用時）。よって
 *   手札の高さ h = (動画の高さ - 630*2) / 2
 *   field2 の上端 = h、field1 の上端 = h + 630
 * となる。**上下の手札の高さが違うとこの2等分が成り立たない**ので、
 * 呼び出し側は configGuessIssue() で先に弾くこと。
 *
 * 横は共通幅に対して中央寄せされる。上側（owner 1）のレイヤーは180°回転して
 * 積まれるため反対の端から詰み、幅が奇数のときは端数の付く側が下と逆になる。
 *
 * **決め打ちの表を引くのではなく計算する。** 手札の高さは config-points の
 * 矩形指定で自由に決まる（GUI既定は420で、実際の収録もそうなっている）ため、
 * 代表的な構成をいくつか並べるやり方では現実のデータに当たらない。
 *
 * @param {number} videoWidth   動画の実サイズ（loadedmetadata から）
 * @param {number} videoHeight
 * @returns {object|null} {video_width, video_height, zones} または求められなければ null
 */
export function defaultOverlayZones(videoWidth, videoHeight) {
  if (!videoWidth || !videoHeight) return null;
  // field レイヤー2枚より低い、または基準より狭い＝同梱マットの構成ではない
  if (videoHeight < FIELD_H * 2 || videoWidth < FIELD_W) return null;

  // 上下の手札は同じ高さでなければ2等分が成り立たない（configGuessIssue 参照）。
  // config が手元に無いときはそこで弾けないが、揃っているなら残りは必ず偶数
  // （手札なし=0、対称なら 2h）なので、奇数ならその時点で構成が違うと分かる。
  if ((videoHeight - FIELD_H * 2) % 2 !== 0) return null;
  const handHeight = (videoHeight - FIELD_H * 2) / 2;
  // 手札レイヤーは指定しても base_size を下回らない（_resolve_layer_sizes が
  // max を取る）ので、0 と 270 の間の高さは有り得ない。ここに落ちるのは
  // 合成の構成が違う（生カメラ映像・拡大された動画など）ということなので、
  // 黙ってズレた枠を出すより「判別できません」にする。
  if (handHeight !== 0 && handHeight < HAND_MIN_H) return null;

  const padX = Math.floor((videoWidth - FIELD_W) / 2);
  // owner 1 = field2（上・180°回転して反対の端から）、owner 0 = field1（下）
  const layerLeft = { 1: videoWidth - FIELD_W - padX, 0: padX };
  const layerTop  = { 1: handHeight, 0: handHeight + FIELD_H };

  const zones = {};
  for (const [key, z] of Object.entries(LAYER_ZONES)) {
    const owner = Number(key.split(':')[1]);
    zones[key] = { x: z.x + layerLeft[owner], y: z.y + layerTop[owner], w: z.w, h: z.h };
  }
  return { video_width: videoWidth, video_height: videoHeight, zones };
}
